import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Volume2 } from "lucide-react";
import { useAutoSave } from "@/hooks/useAutoSave";
import { VoiceAgent } from "@/services/voiceAgentService";
import { VoiceOption } from "@/components/voice/VoiceCard";
import { useVoicePreview } from "@/hooks/useVoicePreview";
import { SUPPORTED_LANGUAGES } from "@/constants/languages";
import SectionHeader from "@/components/agent/shared/SectionHeader";

const voiceSettingsSchema = z.object({
  voice_provider: z.string().default("11labs"),
  voice_id: z.string().default("1SM7GgM6IMuvQlz2BwM3"),
  background_sound_enabled: z.boolean().default(true),
  language: z.string().default("en-US"),
});

type VoiceSettingsFormValues = z.infer<typeof voiceSettingsSchema>;

interface VoiceAgentVoiceFormProps {
  agent: VoiceAgent;
  updateAgent: (data: Partial<VoiceAgent>) => Promise<boolean>;
  showOnly?: string[];
}

// Voice options - popular voices displayed first
const voiceOptions: VoiceOption[] = [
  // Popular voices
  {
    value: "11labs-roger",
    label: "Roger",
    provider: "11labs",
    voice_id: "acvno0b1nAqxHuePHC9S",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/aRyFxDSoSMboX72z5EVApZFrcdu2/voices/acvno0b1nAqxHuePHC9S/cbcf3a08-f9bf-453f-8de9-33db9e3d7847.mp3",
    description: "Super realistic American middle-aged phone voice",
    gender: "male",
    language: "🇺🇸",
    isPopular: true,
  },
  {
    value: "11labs-serena",
    label: "Serena",
    provider: "11labs",
    voice_id: "2nLw85fEHKjAFYrOoWyB",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/aRyFxDSoSMboX72z5EVApZFrcdu2/voices/2nLw85fEHKjAFYrOoWyB/acedde33-c7dd-4c24-9598-f4a45b8a68c7.mp3",
    description: "Super realistic American middle-aged with mic effect",
    gender: "female",
    language: "🇺🇸",
    isPopular: true,
  },

  // Other voices
  {
    value: "11labs-max",
    label: "Max",
    provider: "11labs",
    voice_id: "1SM7GgM6IMuvQlz2BwM3",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/y7Wmp2r8nCeGkLiXiwMOVWktro13/voices/1SM7GgM6IMuvQlz2BwM3/y94G1rGixaqL2FvP3Tte.mp3",
    description: "Professional and clear voice",
    gender: "male",
    language: "🇺🇸",
  },
  {
    value: "11labs-georges",
    label: "Georges",
    provider: "11labs",
    voice_id: "c6SfcYrb2t09NHXiT80T",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/workspace/ae5cf0d6fb064897b39505ab5988252a/voices/c6SfcYrb2t09NHXiT80T/1QliBVcnmK12SQaPoUNj.mp3",
    description:
      "Warm, smooth and versatile American voice that's expressive and dynamic.",
    gender: "male",
    language: "🇺🇸",
  },
  {
    value: "11labs-carl",
    label: "Carl",
    provider: "11labs",
    voice_id: "SF9uvIlY93SJRMdV5jeP",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/WVrNWXY3EDMgxhOLnxkskFZWJZo2/voices/SF9uvIlY93SJRMdV5jeP/242a626d-6d37-4f88-956f-e9c8abcbec5f.mp3",
    description:
      "Middle-aged American male with calm tone. Works well for casual conversations.",
    gender: "male",
    language: "🇺🇸",
  },
  {
    value: "11labs-charles",
    label: "Charles",
    provider: "11labs",
    voice_id: "jn34bTlmmOgOJU9XfPuy",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/bWQZiScBykhesV6qaG0MF9qwmh72/voices/jn34bTlmmOgOJU9XfPuy/595b8ce0-c2c2-4f20-afbe-3918bfba543b.mp3",
    description: "60 years old man, speaking calmly.",
    gender: "male",
    language: "🇺🇸",
  },
  {
    value: "11labs-sarah",
    label: "Sarah",
    provider: "11labs",
    voice_id: "dvcWDtDpjMuyqdgVg3Hu",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/YvMjUeB9B7S6D3fTOzmN8bwZf8z2/voices/dvcWDtDpjMuyqdgVg3Hu/ceCHZfAzagxjVDv4sUZJ.mp3",
    description:
      "American female with neutral accent, friendly professional lecturer perfect for trainings and education.",
    gender: "female",
    language: "🇺🇸",
  },
  {
    value: "11labs-mary",
    label: "Mary",
    provider: "11labs",
    voice_id: "X03mvPuTfprif8QBAVeJ",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/lHEBoKPDRDgUVLw0JK53ttwIkyX2/voices/X03mvPuTfprif8QBAVeJ/KwNZ7ZeTMPsn8NNLEigk.mp3",
    description: "Easy to listen to with a friendly, real vibe.",
    gender: "female",
    language: "🇺🇸",
  },
  {
    value: "11labs-clara",
    label: "Clara",
    provider: "11labs",
    voice_id: "zWoalRDt5TZrmW4ROIA7",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/SorhkqiWFwTdRHnNOO6XqBJOxVJ3/voices/zWoalRDt5TZrmW4ROIA7/c3b6e745-8ca2-456e-af78-69a140788a97.mp3",
    description:
      "African American New Yorker voice that's urban, confident and conversational.",
    gender: "female",
    language: "🇺🇸",
  },
  {
    value: "11labs-victoria",
    label: "Victoria",
    provider: "11labs",
    voice_id: "9TwzC887zQyDD4yBthzD",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/apiwwxzxOkbnkinjLhFNFWfbrnL2/voices/9TwzC887zQyDD4yBthzD/yfB1WlLvJDiStSkwbcCa.mp3",
    description:
      "Professional British female voice - well-spoken and approachable",
    gender: "female",
    language: "🇬🇧",
  },
  {
    value: "11labs-james",
    label: "James",
    provider: "11labs",
    voice_id: "8sZxD42zKDvoEXNxBTdX",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/oKFP1Pfkh5gcTZWmAsgq3C9zZwA3/voices/8sZxD42zKDvoEXNxBTdX/Rvrzgrpw7SCjN87xgiKi.mp3",
    description:
      "Genuine conversationalist - confident and clear American voice",
    gender: "male",
    language: "🇺🇸",
  },
  {
    value: "11labs-actionrob",
    label: "Action Rob",
    provider: "11labs",
    voice_id: "otkcD02TGxFtKuSAt3O3",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/iRv27lRCYdg4ShBxzP5DNe8gGRp2/voices/otkcD02TGxFtKuSAt3O3/8c0e3346-0cf0-48ab-a3b1-f91fc6574083.mp3",
    description:
      "Middle-aged American male with confident voice - great for conversations",
    gender: "male",
    language: "🇺🇸",
  },
  {
    value: "11labs-hope",
    label: "Hope",
    provider: "11labs",
    voice_id: "WAhoMTNdLdMoq1j3wf3I",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/sD92HnMHS9WZLXKNTKxmnC8XmJ32/voices/WAhoMTNdLdMoq1j3wf3I/OdHq2JoogTP6B2rbqeKz.mp3",
    description: "Conversational soft-spoken American voice",
    gender: "female",
    language: "🇺🇸",
  },
  {
    value: "11labs-chris",
    label: "Chris",
    provider: "11labs",
    voice_id: "2EVscXwJhGYuLiX1PgKA",
    previewUrl:
      "https://storage.googleapis.com/eleven-public-prod/database/user/FzPeFfBDCYTY0KhGgxBizxdlVex1/voices/2EVscXwJhGYuLiX1PgKA/Wvla1UkJ6lnU0Sv3Wavh.mp3",
    description: "Yorkshire English accent - conversational and expressive",
    gender: "male",
    language: "🇬🇧",
  },
];

const VoiceAgentVoiceForm = ({
  agent,
  updateAgent,
  showOnly,
}: VoiceAgentVoiceFormProps) => {
  const { currentlyPlaying, isLoading, play, isPlaying } = useVoicePreview();

  // Get current voice selection based on provider + voice_id
  const getCurrentVoiceValue = () => {
    const provider = agent.voice_provider || "11labs";
    const voiceId = agent.voice_id || "1SM7GgM6IMuvQlz2BwM3";
    // Find the matching voice option
    const voiceOption = voiceOptions.find(
      (v) => v.provider === provider && v.voice_id === voiceId,
    );
    return voiceOption ? voiceOption.value : "11labs-max";
  };

  const [selectedVoice, setSelectedVoice] = useState(getCurrentVoiceValue());

  const [initialFormValues, setInitialFormValues] =
    useState<VoiceSettingsFormValues | null>({
      voice_provider: agent.voice_provider || "11labs",
      voice_id: agent.voice_id || "1SM7GgM6IMuvQlz2BwM3",
      background_sound_enabled: agent.background_sound_enabled ?? true,
      language: agent.language || "en-US",
    });

  const form = useForm<VoiceSettingsFormValues>({
    resolver: zodResolver(voiceSettingsSchema),
    defaultValues: {
      voice_provider: agent.voice_provider || "11labs",
      voice_id: agent.voice_id || "1SM7GgM6IMuvQlz2BwM3",
      background_sound_enabled: agent.background_sound_enabled ?? true,
      language: agent.language || "en-US",
    },
  });

  // Update form when agent data changes
  useEffect(() => {
    const newVoiceValue = getCurrentVoiceValue();
    setSelectedVoice(newVoiceValue);

    form.reset({
      voice_provider: agent.voice_provider || "11labs",
      voice_id: agent.voice_id || "1SM7GgM6IMuvQlz2BwM3",
      background_sound_enabled: agent.background_sound_enabled ?? true,
      language: agent.language || "en-US",
    });

    setInitialFormValues({
      voice_provider: agent.voice_provider || "11labs",
      voice_id: agent.voice_id || "1SM7GgM6IMuvQlz2BwM3",
      background_sound_enabled: agent.background_sound_enabled ?? true,
      language: agent.language || "en-US",
    });
  }, [agent, form]);

  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      if (!initialFormValues) return;
      const data = form.getValues();
      return await updateAgent(data);
    },
    successMessage: "Voice settings saved automatically",
    errorMessage: "Failed to auto-save voice settings",
  });

  // Watch for changes
  useEffect(() => {
    if (!initialFormValues) return;
    const handleChange = () => {
      const data = form.getValues();
      const hasChanges = Object.keys(data).some((key) => {
        return (
          data[key as keyof VoiceSettingsFormValues] !==
          initialFormValues[key as keyof VoiceSettingsFormValues]
        );
      });
      if (hasChanges) {
        setDirty();
        setInitialFormValues(data);
      }
    };
    const subscription = form.watch(handleChange);
    return () => subscription.unsubscribe();
  }, [form, setDirty, initialFormValues]);

  const shouldShowField = (fieldName: string) =>
    !showOnly || showOnly.includes(fieldName);

  const handleVoiceChange = (value: string) => {
    const selectedVoiceOption = voiceOptions.find(
      (option) => option.value === value,
    );
    if (selectedVoiceOption) {
      setSelectedVoice(value);
      form.setValue("voice_provider", selectedVoiceOption.provider);
      form.setValue("voice_id", selectedVoiceOption.voice_id);
    }
  };

  const handlePlayPreview = async (voiceValue: string, previewUrl: string) => {
    await play(voiceValue, previewUrl);
  };

  return (
    <Card className="border rounded-2xl">
      <CardContent className="p-5">
        <SectionHeader
          icon={<Volume2 className="h-5 w-5" />}
          title="Voice Settings"
        />

        <Form {...form}>
          <div className="space-y-6">
            {/* Language Selection */}
            {shouldShowField("language") && (
              <FormField
                control={form.control}
                name="language"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Language
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a language" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SUPPORTED_LANGUAGES.map((lang) => (
                          <SelectItem key={lang.code} value={lang.code}>
                            {lang.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Background Sound */}
            {shouldShowField("background_sound_enabled") && (
              <FormField
                control={form.control}
                name="background_sound_enabled"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <div>
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Background Sound
                        </FormLabel>
                        <p className="text-xs text-gray-500">
                          Enable background sound during calls
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Voice Selection - Voice Cards Grid (3 columns) */}
            {shouldShowField("voice_id") && (
              <FormField
                control={form.control}
                name="voice_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Voice
                    </FormLabel>
                    <FormControl>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                        {voiceOptions.map((voice) => {
                          const isSelected = selectedVoice === voice.value;
                          const isPlaying = currentlyPlaying === voice.value;
                          const isLoadingVoice = isLoading === voice.value;

                          return (
                            <div
                              key={voice.value}
                              onClick={() => handleVoiceChange(voice.value)}
                              className={`
                                cursor-pointer transition-all hover:shadow-md border rounded-2xl p-2
                                ${isSelected ? "ring-2 ring-blue-500" : ""}
                              `}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-sm font-medium text-gray-900">
                                    {voice.label}
                                  </span>
                                  {voice.isPopular && (
                                    <span className="text-xs px-1.5 py-0 bg-gray-100 text-gray-600 rounded">
                                      Popular
                                    </span>
                                  )}
                                  <span className="text-sm font-semibold">
                                    {voice.gender === "male" ? (
                                      <span className="text-blue-600">♂</span>
                                    ) : (
                                      <span className="text-pink-600">♀</span>
                                    )}
                                  </span>
                                </div>
                                {voice.previewUrl && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handlePlayPreview(
                                        voice.value,
                                        voice.previewUrl!,
                                      );
                                    }}
                                    className="h-6 w-6 hover:bg-gray-100 rounded flex items-center justify-center"
                                  >
                                    {isLoadingVoice ? (
                                      <span className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
                                    ) : isPlaying ? (
                                      <span className="w-3 h-3">⏸</span>
                                    ) : (
                                      <span className="w-3 h-3">▶</span>
                                    )}
                                  </button>
                                )}
                              </div>
                              {voice.language && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {voice.language}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Auto-save indicator */}
            {isSaving && (
              <div className="flex justify-end">
                <span className="text-sm text-muted-foreground">
                  Auto-saving…
                </span>
              </div>
            )}
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export default VoiceAgentVoiceForm;
